import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom'
import { AppContext } from '../contexts/AppContext';
import styled from 'styled-components';
import Header from '../framework/Header';
import BackgroundImage from '../components/BackgroundImage';
import ListItem from '../components/ListItem';
import { getTranslation } from '../common/Translation';
import PageTransition from '../components/PageTransition';
import { useTranslation } from 'react-i18next';
import ErrorMessageView from '../components/ErrorMessage';
import useFetchDocument from '../hooks/useFetchDocument';
import { detectDomChanges } from '../common/Common';
import useRememberScrollPosition from '../hooks/useRememberScrollPosition';
import { usePlausiblePageview } from '../hooks/usePlausibleTracker';
import { hex2rgb } from '../common/Media';
import { u } from '../common/Units';

const ListViewport = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: max(${u(60)}, var(--menu-bottom-space, 0px));
  padding-top: ${u(140)};
  box-sizing: border-box;
  overflow: hidden;
  touch-action: pan-x;
  scroll-snap-type: x mandatory;

  &.snapScroll {
    overflow-x: auto;
    overflow-y: hidden;
    /* Scrollbar thumb has 50px of transparent space below it; overlap the menu by that much */
    bottom: max(0px, var(--menu-bottom-space, 0px) - 50px);
  }

  &.snapScroll li:nth-child(1n+1) {
    scroll-snap-align: end;
  }

  /* Do not set scrollbar-width here: Chromium then ignores ::-webkit-scrollbar and shows the native bar */
  &.snapScroll::-webkit-scrollbar {
    height: 90px;
  }

  &.snapScroll::-webkit-scrollbar-thumb {
    border-bottom: 50px solid transparent;
    border-top: 30px solid transparent;
    background-color: ${p => p.theme.accentColor};
    background-clip: padding-box;
  }

  &.snapScroll::-webkit-scrollbar-track {
    margin-left: ${u(65)};
    margin-right: ${u(65)};
  }
`

const ListWrapper = styled.ul`
  list-style: none;
  display: grid;
  align-items: center;
  justify-items: center;
  justify-content: start;
  align-content: space-evenly;
  grid-template-areas: none;
  grid-auto-flow: row dense;
  gap: 12px;
  height: 100%;
  width: max-content;
  margin: 0;
  /* Side padding scrolls with the cells: visible at the start and end, not a clip */
  padding: 0 ${u(65)};
  box-sizing: content-box;
  /* Snap to the padded end so mandatory item snaps do not pull back and hide the gutter */
  scroll-snap-align: end;
`

interface ListViewProps {
  previewData?: any,
  overrideTitle?: string
}

/**
 * Render a list of articles or media objects
 * @returns {JSX.Element} Component template
 */
const MosaicView: FC<ListViewProps> = ({previewData=null, overrideTitle}) => {
  const { t } = useTranslation();
  const { documentId } = useParams<{documentId: string}>();
  const { deviceId, currentLanguage } = useContext(AppContext);
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [columnCount, setColumnCount] = useState<number>(3);
  const [rowCount, setRowCount] = useState<number>(2);
  const [gridGap, setGridGap] = useState<number>(20);
  const [titleColor, setTitleColor] = useState<string>("#ffffff");
  const [subtitleColor, setSubtitleColor] = useState<string>("#ffffff");
  const [titleSize, setTitleSize] = useState<string>("1.5em");
  const [subtitleSize, setSubtitleSize] = useState<string>("1em");
  const [textPosition, setTextPosition] = useState<string>("bottom-left");
  const [textBackground, setTextBackground] = useState<string>("shadow");
  const [cellBackgroundColor, setCellBackgroundColor] = useState<string>();
  const [cellFilterColor, setCellFilterColor] = useState<string>();
  const [gridFlow, setGridFlow] = useState<string>("row");
  const [data, error] = useFetchDocument(documentId, previewData);
  const scrollPosition = useRef<any>({x: 0, y: 0});
  usePlausiblePageview({ deviceId: String(deviceId), documentId: documentId, documentType: 'mosaic', ownerId: data?.owner_id, ownerSlug: data?.owner_slug, language: currentLanguage}, { waitFor: ['ownerSlug'] });
  useRememberScrollPosition(data, viewportRef, scrollPosition);

  /**
   * Check if scrollbar should be visible
   */
  const checkScrollbarVisibility = useCallback(() => {
    setTimeout(() => {
      if (viewportRef.current) {
        viewportRef.current.classList.remove("snapScroll");
        // Ignore sub-pixel overflow from layout rounding
        if (viewportRef.current.scrollWidth > viewportRef.current.clientWidth + 2) {
          viewportRef.current.classList.add("snapScroll");
        }
      }
    }, 100);
  }, []);

  // Store scroll position
  const updateScrollPosition = useCallback((e: any) => {
    scrollPosition.current = { x: e.target.scrollLeft, y: e.target.scrollTop }
  }, []);

  // Get the grid size. Can be received as a string ("2x3") or an object ({rows: 2, columns: 3})
  useEffect(() => {
    if (typeof data?.content?.settings?.grid?.gridSize === "string") {
      const gridSize = data?.content?.settings?.grid?.gridSize;
      setColumnCount(Number(gridSize?.split("x")?.[0] || columnCount))
      setRowCount(Number(gridSize?.split("x")?.[1] || rowCount))
    } else {
      setRowCount(data?.content?.settings?.grid?.rows || rowCount);
      setColumnCount(data?.content?.settings?.grid?.columns || columnCount);
    }
      
    setCellBackgroundColor(hex2rgb(data?.content?.settings?.grid?.backgroundColor, data?.content?.settings?.grid?.backgroundOpacity))
    setCellFilterColor(hex2rgb(data?.content?.settings?.grid?.filterColor, data?.content?.settings?.grid?.filterOpacity))

    setGridFlow(data?.content?.settings?.grid?.flow || "row");

    checkScrollbarVisibility();
  }, [data?.content?.settings, hex2rgb, columnCount, rowCount, checkScrollbarVisibility]);

  // Update grid parameters: rows, columns and grid gap
  useEffect(() => {
    const applyGrid = () => {
      if (!listRef.current || !viewportRef.current) { return; }
      // Columns are sized to the visible mosaic, minus the same gutter on both sides
      const columnWidth = `calc((${viewportRef.current.clientWidth}px - ${u(130)}) / ${columnCount} - ${(gridGap * (columnCount - 1) / columnCount)}px)`;
      listRef.current.style.gridTemplateRows = `repeat(${rowCount}, calc(${100/rowCount}% - ${(rowCount * gridGap/rowCount) - (gridGap/rowCount)}px))`;
      listRef.current.style.gridTemplateColumns = `repeat(${columnCount}, ${columnWidth})`;
      listRef.current.style.gridAutoColumns = columnWidth;
      listRef.current.style.gap = `${gridGap}px`;
      listRef.current.style.gridAutoFlow = `${gridFlow} dense`;
    };

    applyGrid();
    window.addEventListener("resize", applyGrid);
    return () => window.removeEventListener("resize", applyGrid);
  }, [rowCount, columnCount, gridGap, gridFlow]);

  // Should the scrollbar be visible?
  useEffect(() => {
    return detectDomChanges(listRef, () => {
      checkScrollbarVisibility();
    });
  }, [checkScrollbarVisibility]);

  // Set grid and text states to be used in the MosaicItem component
  useEffect(() => {
    setGridGap(data?.content?.settings?.grid?.gap || gridGap);
    setTitleColor(data?.content?.settings?.text?.titleColor || "#ffffff");
    setSubtitleColor(data?.content?.settings?.text?.subtitleColor || "#ffffff");
    setTitleSize(data?.content?.settings?.text?.titleSize || titleSize);
    setSubtitleSize(data?.content?.settings?.text?.subtitleSize || subtitleSize);
    setTextPosition(data?.content?.settings?.text?.textPosition || textPosition);
    setTextBackground(data?.content?.settings?.text?.textBackground || textBackground);
  }, [data, rowCount, columnCount, gridGap, titleColor, subtitleColor, titleSize, subtitleSize, textPosition, textBackground]);

  // Do layout adjustments based on items count if grid size is not specified
  useEffect(() => {
    let rows = 0, cols = 0;
    const itemsCount = data?.content?.records?.list?.length;
    const gridSettings: any = data?.content?.settings?.grid;

    // If no row counter is specified
    if (isNaN(gridSettings?.rows)) {
      if (itemsCount <= 3) {
        rows = 1;
      } else {
        rows = 2;
      }

      listRef.current && setRowCount(rows);
    }

    // If no column counter is specified
    if (isNaN(gridSettings?.columns)) {
      if (itemsCount <= 3) {
        cols = itemsCount;
      } else if (itemsCount === 4) {
        cols = 2;
      } else {
        cols = 3;
      }

      listRef.current && setColumnCount(cols);
    }
  }, [data, rowCount, columnCount]);

  // Display error message if no content is loaded
  if (error) { return <ErrorMessageView title={t("presentationNotFound.title")} body={t("presentationNotFound.body")} redirect={true}></ErrorMessageView> }

  // Only display if published
  if (data?.status && data?.status !== "published") { return <ErrorMessageView title={t("presentationNotPublished.title")} body={t("presentationNotPublished.body")} redirect={true}/> }

  return (
    <>
      <Header title={getTranslation(overrideTitle, currentLanguage) || (data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
      <PageTransition waitFor={data}>
        <BackgroundImage media={data?.content?.background?.image} backgroundColor={data?.content?.background?.backgroundColor} />
        <ListViewport ref={viewportRef} className="mosaic scroll" onScroll={updateScrollPosition}>
          <ListWrapper ref={listRef}>
            {data?.content?.records?.list?.map((item: any, i: number) => {
              return <ListItem key={`listìtem-${i}`} documentId={item?.to_document_id} image={item?.content?.image} title={item?.content?.title} subtitle={item?.content?.subtitle} cellSize={item?.content?.cellSize} textPosition={textPosition} titleColor={titleColor} subtitleColor={subtitleColor} titleSize={titleSize} subtitleSize={subtitleSize} textBackground={textBackground} cellBackgroundColor={cellBackgroundColor} cellFilterColor={cellFilterColor}/>
            })}
          </ListWrapper>
        </ListViewport>
      </PageTransition>
    </>
  );
}

export default MosaicView;
